// src/components/MainCanvas.jsx
import React, { useContext, useEffect, useState, useMemo, useRef } from 'react';
import {
	DeckContext,
	getEdgeColor,
	DeckBoxEdgeTypes,
	BumpoutEdgeTypes,
	StairEdgeTypes,
	getEdgeBorderColor,
	LandingEdgeTypes,
} from '../context/DeckContextV3';
import DeckBox from './blocks/DeckBoxV3';
import {
	Box,
	Typography,
	ButtonGroup,
	IconButton,
	Divider,
	useTheme,
} from '@mui/material';
import { TransformWrapper, TransformComponent } from 'react-zoom-pan-pinch';
import { Add, Remove, Restore } from '@mui/icons-material';
import BlockType from '../../../../../enums/blockType';
import Stair from './blocks/StairV3';
import Landing from './blocks/landingV3';
import BlockModal from './popUps/V3/BlockModalV3';
import Bumpout from './blocks/BumpoutV3';
import EdgeModalV3 from './popUps/V3/EdgeModalV3/EdgeModalV3';
import ScaleGridBox from './ScaleGridBox';
import { CanvasFullScreenToggleButton } from 'src/globalComponents/canvasFullScreenToggleButton';
import AddBlockPopper from './addBlock/AddBlockPopper';
import { calculateCanvasBounds } from '../utils/calculateCanvasBounds';
import { useQuoteState } from 'src/stateProviders/QuoteStateProvider';
import DropbeamNeededModal from './popUps/dropbeamNeededModal';
import DropbeamNotNeededModal from './popUps/dropbeamNotNeededModal';

const MainCanvasV3 = () => {
	const { state, dispatch, transformWrapperRef } = useContext(DeckContext);
	const quoteState = useQuoteState();
	const [isDragging, setIsDragging] = useState(false);
	const [scrollPosition, setScrollPosition] = useState({
		scrollLeft: 0,
		scrollTop: 0,
	});

	const [canvasDimensions, setCanvasDimensions] = useState({
		width: 0,
		height: 0,
	});

	const theme = useTheme();
	const isTouchDevice = window.matchMedia('(pointer: coarse)').matches;

	const prevBlocksRef = useRef(state.blocks);
	useEffect(() => {
		if (prevBlocksRef.current !== state.blocks) {
			prevBlocksRef.current = state.blocks;
		}
	}, [state.blocks]);

	// Calculate canvas bounds dynamically
	const canvasBounds = useMemo(() => {
		return calculateCanvasBounds(state.blocks, state.scaleFactor);
	}, [state.blocks, state.scaleFactor]);

	// Update canvas bounds in context
	useEffect(() => {
		dispatch({ type: 'SET_CANVAS_BOUNDS', payload: canvasBounds });
	}, [canvasBounds, dispatch, state.blocks.length]);

	useEffect(() => {
		const canvasElement = document.getElementById('main-canvas');
		if (!canvasElement) return;

		let scrollTimeout = null;
		let resizeTimeout = null;

		const handleScroll = () => {
			if (scrollTimeout) return;

			scrollTimeout = setTimeout(() => {
				setScrollPosition({
					scrollLeft: canvasElement.scrollLeft,
					scrollTop: canvasElement.scrollTop,
				});
				scrollTimeout = null;
			}, 16);
		};

		const handleResize = () => {
			if (resizeTimeout) clearTimeout(resizeTimeout);

			resizeTimeout = setTimeout(() => {
				setCanvasDimensions({
					width: canvasElement.clientWidth,
					height: canvasElement.clientHeight,
				});
			}, 100);
		};

		setScrollPosition({
			scrollLeft: canvasElement.scrollLeft,
			scrollTop: canvasElement.scrollTop,
		});
		setCanvasDimensions({
			width: canvasElement.clientWidth,
			height: canvasElement.clientHeight,
		});

		canvasElement.addEventListener('scroll', handleScroll, { passive: true });

		const resizeObserver = new ResizeObserver(handleResize);
		resizeObserver.observe(canvasElement);

		return () => {
			if (scrollTimeout) clearTimeout(scrollTimeout);
			if (resizeTimeout) clearTimeout(resizeTimeout);
			canvasElement.removeEventListener('scroll', handleScroll);
			resizeObserver.disconnect();
		};
	}, []);

	const deckboxes = state.blocks.filter((blk) => blk.type == BlockType.DECKBOX);
	const landings = deckboxes.flatMap((dk) => dk.landings);
	const stairs = deckboxes.flatMap((dk) => dk.stairs);
	const bumpouts = deckboxes.flatMap((dk) => dk.bumpouts);

	useEffect(() => {
		if (isDragging) {
			dispatch({ type: 'CLOSE_BLOCK_MODAL' });
			dispatch({
				type: 'SELECT_EDGE',
				payload: { blockId: null, edgeLabel: null },
			});
		}
	}, [isDragging]);

	const handleSelect = (block) => {
		dispatch({ type: 'SELECT_BLOCK', payload: block });
		dispatch({ type: 'OPEN_BLOCK_MODAL', payload: block });
	};

	const handleUpdateBlock = (updatedBlock) => {
		dispatch({ type: 'UPDATE_BLOCK', payload: updatedBlock });
	};

	const renderEdgeLegend = () => {
		const { selectedBlock } = state;

		if (!selectedBlock) return null;

		let edgeTypes = [];
		switch (selectedBlock.type) {
			case 'DeckBox':
				edgeTypes = DeckBoxEdgeTypes;
				break;
			case 'Bumpout':
				edgeTypes = BumpoutEdgeTypes;
				break;
			case 'Stair':
				edgeTypes = StairEdgeTypes;
				break;
			case 'Landing':
				edgeTypes = LandingEdgeTypes;
				break;
			default:
				edgeTypes = [];
		}

		return (
			<Box
				sx={{
					width: '150px',
					position: 'absolute',
					left: scrollPosition.scrollLeft + canvasDimensions.width - 165,
					top: scrollPosition.scrollTop + 160,
					pointerEvents: 'auto',
					zIndex: '20',
					display: 'flex',
					flexDirection: 'column',
					justifyContent: 'start',
					alignItems: 'start',
					gap: '15px',
					backgroundColor: theme.palette.common.white,
					boxShadow: '0px 3px 10px rgba(0, 0, 0, 0.3)',
					padding: '5px',
					borderRadius: '5px',
				}}
			>
				<Typography
					sx={{
						color: '#A9A9A9',
						fontSize: '13px',
						fontWeight: '600',
						fontFamily: 'Inter',
					}}
				>
					Edge Indicators
				</Typography>

				<Box
					sx={{
						width: '100%',
						display: 'flex',
						flexDirection: 'column',
						alignItems: 'space-between',
						gap: '20px',
					}}
				>
					{edgeTypes.map((type) => (
						<Box
							key={type}
							sx={{
								width: '100%',
								display: 'flex',
								justifyContent: 'space-between',
								alignItems: 'center',
								gap: 1,
							}}
						>
							<Typography
								fontWeight="600"
								fontSize="13px"
								color="#505050"
								fontFamily="Inter"
							>
								{type}
							</Typography>

							<Box
								sx={{
									width: 45,
									height: 10,
									borderRadius: '1px',
									backgroundColor: getEdgeColor(type),
									...(getEdgeBorderColor(type) && {
										border: `1.5px solid ${getEdgeBorderColor(type)}`,
									}),
								}}
							/>
						</Box>
					))}
				</Box>
			</Box>
		);
	};

	return (
		<Box
			id="main-canvas"
			flex="1"
			position="relative"
			bgcolor="#f0f0f0"
			overflow="auto"
			sx={{
				width: '100%',
				height: '100%',
				touchAction: 'manipulation',
				// Scrollbar styles - visible on all devices including tablets
				'&::-webkit-scrollbar': {
					width: '10px',
					height: '10px',
					WebkitAppearance: 'none',
				},
				'&::-webkit-scrollbar-track': {
					background: 'rgba(0, 0, 0, 0.05)',
					borderRadius: '5px',
				},
				'&::-webkit-scrollbar-thumb': {
					background: 'rgba(0, 0, 0, 0.3)',
					borderRadius: '5px',
					'&:hover': {
						background: 'rgba(0, 0, 0, 0.5)',
					},
				},
				'& .react-transform-wrapper': {
					overflow: 'visible !important',
					width: '100%',
					height: '100%',
					minWidth: '100%',
					minHeight: '100%',
					touchAction: 'manipulation',
				},
				'& .react-transform-component': {
					overflow: 'visible !important',
					width: '100%',
					height: '100%',
					minWidth: '100%',
					minHeight: '100%',
					touchAction: 'manipulation',
				},
			}}
		>
			<ScaleGridBox
				scaleFactor={state.scaleFactor}
				zoomScale={state.zoomScale}
			/>

			<Box
				sx={{
					position: 'absolute',
					top: 0,
					left: 0,
					right: 0,
					bottom: 0,
					pointerEvents: 'none',
					zIndex: 1000,
				}}
			>
				{/* Zoom Controls */}
				{state.blocks.length > 0 && transformWrapperRef?.current && (
					<Box
						sx={{
							position: 'absolute',
							left: Math.max(
								scrollPosition.scrollLeft + 10,
								Math.min(
									scrollPosition.scrollLeft + canvasDimensions.width - 60,
									scrollPosition.scrollLeft +
										(document.getElementById('main-canvas')?.clientWidth ||
											canvasDimensions.width) -
										70
								)
							),
							top: scrollPosition.scrollTop + 10,
							pointerEvents: 'auto',
							zIndex: 1000,
						}}
					>
						<ButtonGroup
							orientation="vertical"
							variant="contained"
							size="small"
							sx={{
								backgroundColor: 'white',
								'.MuiIconButton-root': {
									borderRadius: 0,
									'&:hover': {
										backgroundColor: 'rgba(0, 0, 0, 0.08)',
									},
									'&:active': {
										backgroundColor: 'rgba(0, 0, 0, 0.16)',
									},
								},
								'.MuiIconButton-root.Mui-focusVisible': {
									backgroundColor: 'rgba(0, 0, 0, 0.08)',
								},
							}}
						>
							<IconButton
								onClick={() => transformWrapperRef.current?.zoomIn(0.18)}
								size="small"
								disableRipple
								aria-label="Zoom In"
							>
								<Add />
							</IconButton>
							<Divider orientation="horizontal" />
							<IconButton
								onClick={() => transformWrapperRef.current?.zoomOut(0.18)}
								size="small"
								disableRipple
								aria-label="Zoom Out"
							>
								<Remove />
							</IconButton>
							<Divider orientation="horizontal" />
							<IconButton
								onClick={() => transformWrapperRef.current?.resetTransform()}
								size="small"
								disableRipple
								aria-label="Reset"
							>
								<Restore />
							</IconButton>
						</ButtonGroup>
					</Box>
				)}

				{/* Edge Legend */}
				{renderEdgeLegend()}

				{/* Add Block */}
				{(quoteState.reface ||
					(quoteState.structure?.materialType &&
						quoteState.structure?.joistSpacing)) && (
					<AddBlockPopper
						scrollPosition={scrollPosition}
						canvasDimensions={canvasDimensions}
					/>
				)}

				<CanvasFullScreenToggleButton
					sx={{
						position: 'absolute',
						left: scrollPosition.scrollLeft + 10,
						top: scrollPosition.scrollTop + 10,
						pointerEvents: 'auto',
						zIndex: 1000,
					}}
				/>
			</Box>

			{(quoteState.reface ||
				(quoteState.structure?.materialType &&
					quoteState.structure?.joistSpacing)) && (
				<>
					<TransformWrapper
						ref={transformWrapperRef}
						initialScale={1}
						minScale={0.3333}
						maxScale={2}
						wheel={{ disabled: true }}
						doubleClick={{ disabled: true }}
						panning={{ disabled: isDragging || isTouchDevice }}
						limitToBounds={false}
						centerOnInit={false}
						pinch={{ disabled: false }}
						pan={{ disabled: isDragging || isTouchDevice }}
						zoomAnimation={{ disabled: false }}
						onTransformed={(ref, transformState) => {
							// console.log('[MainCanvasV3] TransformComponent zoom/pan:', {
							// 	scale: transformState.scale,
							// 	positionX: transformState.positionX,
							// 	positionY: transformState.positionY,
							// 	note: 'CSS transform handles all scaling - container stays at base size',
							// });
							dispatch({
								type: 'SET_ZOOM_SCALE',
								payload: transformState.scale,
							});
						}}
					>
						{({ zoomIn, zoomOut, resetTransform }) => (
							<>
								{/* Transformable Content */}
								<TransformComponent>
									<Box
										sx={{
											position: 'relative',
											width: `${canvasBounds.width}px`,
											height: `${canvasBounds.height}px`,
										}}
									>
										<Box
											sx={{
												position: 'absolute',
												top: 0,
												left: 0,
												width: '100%',
												height: '100%',
												transform: `translate(${canvasBounds.offsetX}px, ${canvasBounds.offsetY}px)`,
											}}
											ref={(el) => {
												if (el) {
													// console.log(
													// 	'[MainCanvasV3] Blocks container transform applied:',
													// 	{
													// 		offsetX: canvasBounds.offsetX,
													// 		offsetY: canvasBounds.offsetY,
													// 		transform: `translate(${canvasBounds.offsetX}px, ${canvasBounds.offsetY}px)`,
													// 	}
													// );
												}
											}}
										>
											{state.blocks.map((block) => {
												// console.log('[MainCanvasV3 render] Rendering block:', {
												// 	blockId: block.id,
												// 	blockLabel: block.label,
												// 	position: block.position,
												// 	type: block.type,
												// });
												if (block.type === 'DeckBox') {
													return (
														<DeckBox
															key={block.id}
															label={block.label}
															block={block}
															onSelect={handleSelect}
															onUpdateBlock={handleUpdateBlock}
															setIsDragging={setIsDragging}
														/>
													);
												}
												return null;
											})}

											{/* Render Stairs */}
											{stairs.map((stair) => (
												<Stair
													key={stair.id}
													label={stair.label}
													stair={stair}
													onSelect={handleSelect}
													onUpdateBlock={handleUpdateBlock}
													setIsDragging={setIsDragging}
												/>
											))}

											{/* Render Landings */}
											{landings.map((landing) => (
												<Landing
													key={landing.id}
													label={landing.label}
													landing={landing}
													onSelect={handleSelect}
													onUpdateBlock={handleUpdateBlock}
													setIsDragging={setIsDragging}
												/>
											))}

											{/* Render Bumpouts */}
											{bumpouts.map((bumpout) => (
												<Bumpout
													key={bumpout.id}
													label={bumpout.label}
													bumpout={bumpout}
													onSelect={handleSelect}
													onUpdateBlock={handleUpdateBlock}
													setIsDragging={setIsDragging}
												/>
											))}
										</Box>
									</Box>
								</TransformComponent>
							</>
						)}
					</TransformWrapper>

					{/* Render Block Modal */}
					{state.displayedBlockModal && (
						<BlockModal block={state.displayedBlockModal} />
					)}

					{/* Render Edge Modal */}
					{state.selectedEdge?.blockId && state.selectedEdge?.edgeLabel && (
						<EdgeModalV3 />
					)}

					{state.dropbeamNeeded?.show && (
						<DropbeamNeededModal
							open={state.dropbeamNeeded.show}
							block={state.dropbeamNeeded.block}
							neededDropbeams={state.dropbeamNeeded.dropbeamsNeeded}
							setOpen={(value) => {
								dispatch({
									type: 'SET_DROPBEAM_NEEDED',
									payload: {
										...state.dropbeamNeeded,
										show: value,
									},
								});
							}}
						/>
					)}

					{state.dropbeamNotNeeded?.show && (
						<DropbeamNotNeededModal
							open={state.dropbeamNotNeeded.show}
							block={state.dropbeamNotNeeded.block}
							setOpen={(value) => {
								dispatch({
									type: 'SET_DROPBEAM_NOT_NEEDED',
									payload: {
										...state.dropbeamNotNeeded,
										show: value,
									},
								});
							}}
						/>
					)}
				</>
			)}

			{!quoteState.reface &&
				!(
					quoteState.structure?.materialType &&
					quoteState.structure?.joistSpacing
				) && (
					<Box
						sx={{
							width: '100%',
							height: '100%',
							display: 'flex',
							flexDirection: 'column',
							justifyContent: 'center',
							alignItems: 'center',
							gap: '10px',
						}}
					>
						<Typography
							sx={{
								fontWeight: 'bold',
							}}
						>
							Select Joist Material & Spacing Above
						</Typography>
						<Typography>
							To begin building your deck, you need to select the Structural
							Material first, in order to continue
						</Typography>
					</Box>
				)}
		</Box>
	);
};

export default MainCanvasV3;
